import { useEffect, useMemo, useRef, useState } from 'react'
import { Building2, Mail, UserRound, Users } from 'lucide-react'
import { EditDatePicker } from '@/components/ui/EditDatePicker'
import { inviteTabSnapshot } from '@/features/applicant/utils/inviteTabSnapshot'
import { inviteCheckboxClass } from '@/features/auth/components/inviteOnboardingUi'
import { CO_RESIDENT_RELATIONSHIP_TYPES } from '../../constants'
import { formatPhoneInput } from './aboutYourselfTabHelpers'
import { validateAboutYourselfPersonalTab } from '../../utils/aboutYourselfValidator'
import type { AboutYourselfPersonalFormData } from '../../types/tabs'
import { cn } from '@lib/utils/helpers'
import { formatPhoneNumber, maskSSN } from '@/types/application'
import type { InviteRegistrationView, ResidentApplicationData } from '@/types/application'
import type { ApplicationDetailData, ApplicationLayerData } from '../../types/application'
import type { ApplicationDetailFormDelta } from '../../types/pages'

const lockedInputClass =
  'w-full cursor-default rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800'
const editInputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800'

function formatDob(value?: string) {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) return `${match[2]}/${match[3]}/${match[1]}`
  return value
}

function toDateInput(value?: string) {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function formatRent(value: unknown) {
  if (value == null || value === '') return ''
  const amount = Number(value)
  if (Number.isNaN(amount)) return String(value)
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo`
}

function formatMoveIn(value?: string) {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fieldError(errors: string[], label: string) {
  return errors.find((error) => error.startsWith(label)) ?? ''
}

/** Same digit cap as Identity Verification `formatPhoneInput`. */
function phoneDigits(value: string) {
  return value.replace(/\D/g, '').slice(0, 11)
}

function propertyHasDetails(property: InviteRegistrationView['property']) {
  if (!property) return false
  return Boolean(
    property.property_name ||
      property.property_address ||
      property.rent_amount ||
      property.unit_number ||
      property.invite_code ||
      property.agent_name ||
      property.lease_start_date,
  )
}

function detailSnapshot(
  layer: ApplicationLayerData | null | undefined,
  root: ApplicationDetailData | null | undefined,
): InviteRegistrationView {
  const fromApplicant = inviteTabSnapshot(layer as ResidentApplicationData | null | undefined)
  if (propertyHasDetails(fromApplicant.property)) return fromApplicant

  const otherAgent = root?.other_agent
  const fromApplication = inviteTabSnapshot({
    property: root?.property,
    rent_amount: root?.rent_amount,
    access_fee: root?.access_fee,
    balance_due: root?.balance_due,
    unit_number: root?.unit_number,
    lease_start_date: root?.lease_start_date,
    lease_duration: root?.lease_duration,
    invite_code: root?.invite_code || layer?.applicant_invite_code,
    agent: root?.agent,
    other_agent: {
      first_name: otherAgent?.first_name || otherAgent?.agent_name || '',
      last_name: otherAgent?.last_name || '',
      email: otherAgent?.email || otherAgent?.agent_email || '',
    },
    expected_residents: layer?.expected_residents ?? root?.expected_residents,
  } as ResidentApplicationData)

  return {
    personal: fromApplicant.personal,
    property: fromApplication.property,
  }
}

function draftFromSnapshot(
  personal: InviteRegistrationView['personal'],
): AboutYourselfPersonalFormData {
  return {
    first_name: personal?.first_name || '',
    middle_name: personal?.middle_name || '',
    last_name: personal?.last_name || '',
    email: personal?.email || '',
    mobile_no: phoneDigits(personal?.mobile_no || ''),
    shared_phone_no: personal?.shared_phone_no === '1' || personal?.shared_phone_no === 'true',
    communication_preference: personal?.communication_preference || '',
    birthdate: toDateInput(personal?.birthdate),
    security_number: personal?.security_number || '',
    suffix: '',
  }
}

export function InviteTab({
  applicationAllData,
  applicationData,
  isEdit = false,
  onFormChange,
  exposeInlineValidation = false,
}: {
  applicationAllData?: ApplicationDetailData | null
  applicationData?: ApplicationLayerData | null
  isEdit?: boolean
  onFormChange?: (data: ApplicationDetailFormDelta) => void
  exposeInlineValidation?: boolean
}) {
  const snapshot = useMemo(
    () => detailSnapshot(applicationData, applicationAllData),
    [applicationData, applicationAllData],
  )
  const seeded = useMemo(() => draftFromSnapshot(snapshot.personal), [snapshot])
  const seededRef = useRef(seeded)
  seededRef.current = seeded
  const [draft, setDraft] = useState<AboutYourselfPersonalFormData>(seeded)

  useEffect(() => {
    if (isEdit) setDraft(seededRef.current)
  }, [isEdit, applicationData?.applicant_id])

  const personal = isEdit ? draft : seeded
  const property = snapshot.property ?? {}
  const errors =
    isEdit && exposeInlineValidation
      ? validateAboutYourselfPersonalTab(personal).errors
      : []
  const preference = personal.communication_preference || ''
  const sharedPhone = personal.shared_phone_no
  const phone = formatPhoneNumber(personal.mobile_no) || personal.mobile_no || ''
  const address = property.property_address || ''
  const expected =
    property.expected_residents != null && String(property.expected_residents) !== ''
      ? String(property.expected_residents)
      : ''
  const coApplicants = Object.values(applicationAllData?.co_resident_application ?? {}).filter(
    (person) =>
      person &&
      String(person.applicant_id ?? '') !== String(applicationData?.applicant_id ?? '') &&
      (person.user_data?.first_name || person.user_data?.last_name || person.user_data?.email),
  )

  const update = (field: keyof AboutYourselfPersonalFormData, value: string | boolean) => {
    const next = { ...draft, [field]: value }
    setDraft(next)
    onFormChange?.({
      first_name: next.first_name,
      middle_name: next.middle_name,
      last_name: next.last_name,
      email: next.email,
      mobile_no: next.mobile_no,
      shared_phone_no: next.shared_phone_no,
      communication_preference: next.communication_preference,
      birthdate: next.birthdate,
      security_number: next.security_number,
    })
  }

  return (
    <div className="bg-gray-50 p-3 sm:p-6" data-testid="application-registration-about-tab">
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[1.45fr_1fr]">
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6 md:p-8">
          <div className="mb-6 flex items-center gap-3 border-b border-gray-200 pb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <UserRound className="h-5 w-5 text-blue-600" aria-hidden />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Personal &amp; Contact Information</h2>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TextField
              label="First Name"
              required
              readOnly={!isEdit}
              error={fieldError(errors, 'First Name')}
              value={personal.first_name}
              onChange={(value) => update('first_name', value)}
            />
            <TextField
              label="Middle Name"
              placeholder="Optional"
              readOnly={!isEdit}
              error={fieldError(errors, 'Middle Name')}
              value={personal.middle_name}
              onChange={(value) => update('middle_name', value)}
            />
            <TextField
              label="Last Name"
              required
              readOnly={!isEdit}
              error={fieldError(errors, 'Last Name')}
              value={personal.last_name}
              onChange={(value) => update('last_name', value)}
            />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {isEdit ? (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Date of Birth<span className="text-red-500">*</span>
                </label>
                <EditDatePicker
                  value={personal.birthdate}
                  onChange={(value) => update('birthdate', value)}
                  displayFormat="mdy"
                  placeholder="MM/DD/YYYY"
                  allowPastDates
                  allowFutureDates={false}
                />
                {fieldError(errors, 'Date of Birth') ? (
                  <p className="mt-1 text-xs text-red-600">{fieldError(errors, 'Date of Birth')}</p>
                ) : null}
              </div>
            ) : (
              <TextField
                label="Date of Birth"
                required
                readOnly
                error={fieldError(errors, 'Date of Birth')}
                value={formatDob(personal.birthdate)}
                onChange={() => undefined}
              />
            )}
            <TextField
              label="SSN / ITIN"
              required
              readOnly={!isEdit}
              error={fieldError(errors, 'SSN')}
              value={isEdit ? personal.security_number : maskSSN(personal.security_number)}
              onChange={(value) => update('security_number', value)}
            />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Email"
              required
              readOnly={!isEdit}
              error={fieldError(errors, 'Email')}
              value={personal.email}
              onChange={(value) => update('email', value)}
            />
            <TextField
              label="Phone Number"
              required
              readOnly={!isEdit}
              type={isEdit ? 'tel' : 'text'}
              placeholder={isEdit ? '(xxx) xxx-xxxx' : undefined}
              error={fieldError(errors, 'Mobile Phone')}
              value={isEdit ? formatPhoneInput(personal.mobile_no) : phone}
              onChange={(value) => update('mobile_no', phoneDigits(value))}
            />
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={sharedPhone}
              readOnly={!isEdit}
              disabled={!isEdit}
              className={inviteCheckboxClass}
              onChange={(event) => update('shared_phone_no', event.target.checked)}
            />
            This is a shared phone number.
          </label>

          <div className="mt-5">
            <p className="mb-2 text-sm font-medium text-slate-700">
              Communication Preference<span className="text-red-500">*</span>
            </p>
            <div className="inline-flex w-fit flex-wrap gap-2">
              {([
                ['Email', 'Email'],
                ['Text Msg', 'Text Message'],
              ] as const).map(([value, label]) =>
                isEdit ? (
                  <button
                    key={value}
                    type="button"
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium',
                      preference === value
                        ? 'border-button bg-button text-white'
                        : 'border-slate-300 bg-white text-slate-700',
                    )}
                    onClick={() => update('communication_preference', value)}
                  >
                    {value === 'Email' ? <Mail className="h-4 w-4" aria-hidden /> : null}
                    {label}
                  </button>
                ) : (
                  <span
                    key={value}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium',
                      preference === value
                        ? 'border-button bg-button text-white'
                        : 'border-slate-300 bg-white text-slate-700',
                    )}
                  >
                    {value === 'Email' ? <Mail className="h-4 w-4" aria-hidden /> : null}
                    {label}
                  </span>
                ),
              )}
            </div>
            {fieldError(errors, 'Communication Preference') ? (
              <p className="mt-1 text-xs text-red-600">{fieldError(errors, 'Communication Preference')}</p>
            ) : null}
          </div>
        </section>

        <section
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6 md:p-8"
          data-testid="application-registration-property-readonly"
        >
          <div className="mb-6 flex items-center justify-between gap-3 border-b border-gray-200 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <Building2 className="h-5 w-5 text-blue-600" aria-hidden />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Your Property</h2>
            </div>
            <span className="text-xl font-bold text-gray-900">Invitation details</span>
          </div>

          <div className="flex items-start justify-between gap-3 pb-4">
            <div>
              <p className="text-lg font-semibold text-slate-900">{property.property_name || 'Your Property'}</p>
              {address ? <p className="mt-1 text-sm text-slate-500">{address}</p> : null}
              {property.unit_number ? (
                <p className="text-sm text-slate-500">Unit {property.unit_number}</p>
              ) : null}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-slate-700">Monthly Rent</p>
              <p className="text-lg font-semibold text-emerald-600">{formatRent(property.rent_amount) || '—'}</p>
              {property.lease_start_date ? (
                <p className="text-xs text-slate-500">Move-in: {formatMoveIn(property.lease_start_date)}</p>
              ) : null}
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-4 border-t border-slate-200 py-4 text-sm">
            <div>
              <dt className="text-sm font-medium text-slate-700">Invite Code</dt>
              <dd className="mt-1 font-medium text-slate-800">{property.invite_code || '—'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-700">Leasing Agent</dt>
              <dd className="mt-1 font-medium text-slate-800">{property.agent_name || '—'}</dd>
              {property.agent_email ? (
                <dd className="text-xs text-slate-500">{property.agent_email}</dd>
              ) : null}
            </div>
          </dl>

          {coApplicants.length > 0 ? (
            <div className="border-t border-slate-200 py-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                <Users className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                Co-Applicants / Roommates
              </p>
              <div className="grid grid-cols-4 gap-3 border-b border-slate-200 pb-2 text-sm font-medium text-slate-700">
                <span>Name</span>
                <span>Relationship</span>
                <span>Email</span>
                <span>Phone</span>
              </div>
              {coApplicants.map((person) => {
                const relationship = person.relationship_type
                  ? CO_RESIDENT_RELATIONSHIP_TYPES[person.relationship_type] || person.relationship_type
                  : '—'
                return (
                  <div
                    key={String(person.applicant_id)}
                    className="grid grid-cols-1 gap-2 border-b border-slate-200 py-3 text-sm last:border-b-0 sm:grid-cols-4 sm:gap-3"
                  >
                    <p className="text-slate-800">
                      {[person.user_data?.first_name, person.user_data?.last_name].filter(Boolean).join(' ') || '—'}
                    </p>
                    <p className="text-slate-700">{relationship}</p>
                    <p className="break-all text-slate-700">{person.user_data?.email || '—'}</p>
                    <p className="text-slate-700">
                      {formatPhoneNumber(person.user_data?.mobile_no) || person.user_data?.mobile_no || '—'}
                    </p>
                  </div>
                )
              })}
            </div>
          ) : null}

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-700">
                Expected residents (including children and other roommates)
                <span className="text-red-500">*</span>
              </p>
              <select
                disabled
                value={expected}
                className="w-28 cursor-default rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              >
                <option value={expected}>{expected || '—'}</option>
              </select>
            </div>
            <p className="mt-2 text-xs text-slate-500">This field is required to proceed.</p>
          </div>
        </section>
      </div>
    </div>
  )
}

function TextField({
  label,
  value,
  readOnly,
  required = false,
  error = '',
  type = 'text',
  placeholder,
  onChange,
}: {
  label: string
  value: string
  readOnly: boolean
  required?: boolean
  error?: string
  type?: string
  placeholder?: string
  onChange: (value: string) => void
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="text-red-500">*</span> : null}
      </label>
      <input
        type={type}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        placeholder={placeholder}
        className={readOnly ? lockedInputClass : editInputClass}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  )
}
